import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, X, Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  FaHandshake,
  FaTruck,
  FaChartLine,
  FaShieldAlt,
  FaFileInvoiceDollar
} from 'react-icons/fa';
import HandRupeeIcon from '../icons/HandRupeeIcon';
import PurchaseCartIcon from '../icons/PurchaseCartIcon';
import ChannelFinanceIcon from '../icons/ChannelFinanceIcon';

const domesticServices = [
  {
    icon: FaHandshake,
    title: 'Vendor Finance',
    description: 'Pay your suppliers early without tying up your own cash. Funding Sathi connects you with capital partners who settle your vendor invoices — keeping your supply chain moving.',
    fullDescription: 'Vendor Finance enables you to pay suppliers early without straining your cash flow. We connect you with capital partners who settle your invoices, maintaining smooth supply chain operations.',
    features: ['Early supplier payments', 'Preserved cash flow', 'Stronger vendor relationships', 'Quick onboarding', 'Real-time tracking']
  },
  {
    icon: FaTruck,
    title: 'Dealer Finance',
    description: 'Help your dealers stock more inventory without waiting for their own credit lines. Funding Sathi orchestrates inventory financing so your dealers sell more and you grow faster.',
    fullDescription: 'Dealer Finance helps your dealership network stock more inventory and sell more without waiting for their own credit approvals. We structure financing around your sales cycle.',
    features: ['Inventory financing', 'Stocking loans', 'Seasonal credit lines', 'No dealer credit checks', 'Multi-brand coverage']
  },
  {
    icon: HandRupeeIcon,
    title: 'Working Capital Demand Loan',
    description: 'Access flexible, on-demand capital for day-to-day operations without pledging fixed assets. Bridge payroll, raw materials, or seasonal gaps — you only pay for what you use.',
    fullDescription: 'Working Capital Loans provide flexible, on-demand capital for daily operations without requiring fixed asset collateral. Bridge gaps in payroll, raw materials, or seasonal fluctuations.',
    features: ['No fixed asset pledge', 'Flexible drawdowns', 'Pay for what you use', 'Quick approval', 'Daily operations support']
  },
  {
    icon: ChannelFinanceIcon,
    title: 'Channel Finance',
    description: 'Unlock liquidity at every tier of your distribution network — from distributors to sub-dealers to retailers. Funding Sathi structures multi-tier financing programs built around your sales cycle.',
    fullDescription: 'Channel Finance unlocks liquidity across your entire distribution network. We structure multi-tier financing from distributors to sub-dealers to retailers, aligned with your sales cycle.',
    features: ['Multi-tier coverage', 'Distributor financing', 'Sub-dealer support', 'Retailer programs', 'Sales cycle alignment']
  },
  {
    icon: FaChartLine,
    title: 'Private Credit',
    description: 'Access institutional-scale capital for strategic growth, expansion, or special projects. Funding Sathi connects mid-to-large enterprises with private credit funds and custom repayment terms.',
    fullDescription: 'Private Credit provides institutional-scale capital for strategic initiatives. We connect mid-to-large enterprises with private credit funds offering customized repayment structures.',
    features: ['Institutional-scale capital', 'Strategic growth funding', 'Custom repayment terms', 'Expansion financing', 'Private credit fund access']
  },
  {
    icon: FaShieldAlt,
    title: 'Special Situation Funding',
    description: 'Facing a gap that banks won\'t fill? Bridge funding, urgent liquidity, or stressed situations — we structure solutions traditional lenders can\'t touch.',
    fullDescription: 'Special Situation Funding addresses gaps that traditional banks cannot fill. We provide bridge funding, urgent liquidity, and solutions for stressed situations with flexible structures.',
    features: ['Bridge funding', 'Urgent liquidity', 'Stressed situation support', 'Non-bank solutions', 'Flexible structuring']
  },
  {
    icon: FaFileInvoiceDollar,
    title: 'Invoice Discounting',
    description: 'Convert your unpaid invoices into immediate cash without waiting for customer payment cycles. Funding Sathi connects you with capital partners who settle your invoices within 48 hours.',
    fullDescription: 'Invoice Discounting converts your unpaid invoices into immediate cash. We connect you with capital partners who settle invoices within 48 hours, eliminating payment cycle delays.',
    features: ['Immediate cash conversion', '48-hour settlement', 'No waiting for customers', 'Verified invoice backing', 'Working capital boost']
  },
  {
    icon: PurchaseCartIcon,
    title: 'Purchase Finance',
    description: 'Finance your procurement seamlessly while extending your payment cycle. Funding Sathi connects businesses with capital partners who fund purchases so you can pay after your sales cycle completes.',
    fullDescription: 'Purchase Finance enables seamless procurement financing with extended payment cycles. We connect you with capital partners who fund purchases, allowing payment after your sales cycle.',
    features: ['Procurement financing', 'Extended payment terms', 'Sales cycle alignment', 'Seamless funding', 'Working capital preservation']
  }
];

const OurServices = () => {
  const [selectedService, setSelectedService] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [currentIndex, setCurrentIndex] = useState(0);
  const scrollContainerRef = useRef(null);

  // Auto-scroll effect - continuous scroll with smooth reset
  useEffect(() => {
    const interval = setInterval(() => {
      if (scrollContainerRef.current) {
        const container = scrollContainerRef.current;
        const scrollWidth = container.scrollWidth;
        const clientWidth = container.clientWidth;
        const maxScroll = scrollWidth - clientWidth;
        const cardWidth = 340 + 24; // card width + gap
        
        setCurrentIndex((prevIndex) => {
          let nextIndex = prevIndex + 1;
          const targetScroll = nextIndex * cardWidth;
          
          // If we've reached the end, reset to beginning
          if (targetScroll >= maxScroll) {
            // Reset without animation for seamless loop
            setTimeout(() => {
              container.scrollTo({
                left: 0,
                behavior: 'auto'
              });
            }, 100);
            return 0;
          }
          
          // Smooth scroll forward
          container.scrollTo({
            left: targetScroll,
            behavior: 'smooth'
          });
          
          return nextIndex;
        });
      }
    }, 1500); // Auto-scroll every 1.5 seconds

    return () => clearInterval(interval);
  }, []);

  return (
    <section className="py-20 bg-gradient-to-b from-white to-gray-50/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="inline-block px-4 py-1.5 bg-[#5C1F33]/10 text-[#5C1F33] rounded-full text-sm font-medium mb-4">
            Our Services
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#333333] mb-4">
            Comprehensive <span className="text-[#5C1F33]">Financing</span> Solutions
          </h2>
          <p className="text-[#666666] text-lg max-w-2xl mx-auto leading-relaxed">
            Tailored financial services designed to accelerate your business growth
          </p>
        </motion.div>
        {/* Services Grid - Horizontal scrollable cards */}
        <div 
          ref={scrollContainerRef}
          className="flex gap-6 overflow-x-auto pb-4 snap-x snap-mandatory scrollbar-hide" 
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {domesticServices.map((service, index) => {
            const isSelected = selectedService?.title === service.title;
            return (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.2, delay: index * 0.08, ease: [0.4, 0, 0.2, 1] }}
                whileHover={{ y: -8, transition: { duration: 0.3 } }}
                onClick={() => setSelectedService(service)}
                className="group relative bg-white rounded-2xl p-6 sm:p-8 shadow-[0_4px_20px_rgba(0,0,0,0.05)] border border-gray-100/50 hover:shadow-[0_20px_40px_rgba(92,31,51,0.12)] transition-all duration-500 text-center cursor-pointer overflow-hidden flex-shrink-0 w-[300px] sm:w-[340px] snap-start"
              >
                {/* Animated Gradient Border */}
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-[#5C1F33]/0 via-[#5C1F33]/0 to-[#5C1F33]/0 group-hover:from-[#5C1F33]/10 group-hover:via-[#5C1F33]/5 group-hover:to-transparent transition-all duration-500" />
                
                {/* Top Accent Line */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-0 h-1 bg-gradient-to-r from-[#5C1F33] to-[#800020] rounded-full group-hover:w-1/2 transition-all duration-500" />
                
                <div className="relative z-10">
                  {/* Icon with enhanced background */}
                  <div className={`w-16 h-16 mx-auto mb-5 rounded-2xl flex items-center justify-center transition-all duration-500 shadow-sm ${isSelected ? 'bg-[#5C1F33] ring-2 ring-white ring-offset-2 ring-offset-[#5C1F33]/10' : 'bg-[#5C1F33]/10 group-hover:bg-[#5C1F33] group-hover:shadow-lg'}`}>
                    <service.icon
                      className={`w-8 h-8 transition-all duration-300 ${isSelected ? 'text-white' : 'text-[#4A4A4A] group-hover:text-white'}`}
                      style={isSelected ? { color: '#ffffff', fill: '#ffffff', stroke: '#ffffff' } : undefined}
                    />
                  </div>
                  
                  <h3 className="text-lg font-bold text-[#333333] mb-3 group-hover:text-[#5C1F33] transition-colors duration-300">{service.title}</h3>
                  <p className="text-sm text-[#666666] mb-5 leading-relaxed">{service.description}</p>
                  
                  <button 
                    onClick={() => setSelectedService(service)}
                    className="inline-flex items-center gap-2 text-sm font-semibold text-[#5C1F33] bg-[#5C1F33]/5 px-4 py-2 rounded-full group-hover:bg-[#5C1F33] group-hover:text-white transition-all duration-300"
                  >
                    Learn More
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Service Detail Modal */}
        <AnimatePresence>
          {selectedService && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={() => setSelectedService(null)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-8 shadow-[0_25px_50px_rgba(0,0,0,0.25)] border border-gray-100"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Modal Header with Gradient Background */}
                <div className="flex items-start justify-between mb-6">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-[#5C1F33] to-[#800020] rounded-2xl flex items-center justify-center shadow-lg">
                      <selectedService.icon className="w-8 h-8 text-white" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-[#333333]">{selectedService.title}</h3>
                      <p className="text-sm text-[#5C1F33] font-medium">Premium Service</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setSelectedService(null)}
                    className="p-2.5 hover:bg-gray-100 rounded-full transition-all duration-300 hover:rotate-90"
                  >
                    <X className="w-6 h-6 text-[#5C1F33] hover:text-[#800020]" />
                  </button>
                </div>

                {/* Full Description with better styling */}
                <div className="bg-gray-50 rounded-xl p-5 mb-6">
                  <p className="text-gray-700 leading-relaxed">
                    {selectedService.fullDescription}
                  </p>
                </div>

                {/* Key Features with enhanced styling */}
                <div className="mb-8">
                  <h4 className="text-sm font-bold text-[#5C1F33] uppercase tracking-wider mb-4 flex items-center gap-2">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#5C1F33]" />
                    Key Features
                  </h4>
                  <div className="grid gap-3">
                    {selectedService.features.map((feature, index) => (
                      <motion.div 
                        key={index} 
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="flex items-start gap-3 p-3 bg-white border border-gray-100 rounded-xl hover:border-[#5C1F33]/20 transition-colors"
                      >
                        <div className="w-6 h-6 bg-gradient-to-br from-[#5C1F33] to-[#800020] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 shadow-md">
                          <Check className="w-3.5 h-3.5 text-white" />
                        </div>
                        <span className="text-sm text-gray-700 font-medium">{feature}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>

                {/* CTA Buttons */}
                <div className="flex gap-4">
                  <Link
                    to="/contact"
                    onClick={() => setSelectedService(null)}
                    className="flex-1 bg-gradient-to-r from-[#5C1F33] to-[#800020] text-white py-3.5 px-6 rounded-xl font-semibold text-center shadow-lg shadow-[#5C1F33]/25 hover:shadow-xl hover:shadow-[#5C1F33]/30 transition-all duration-300"
                  >
                    Apply Now
                  </Link>
                  <button
                    onClick={() => setSelectedService(null)}
                    className="px-6 py-3.5 border-2 border-gray-200 rounded-xl font-semibold text-gray-600 hover:border-[#5C1F33] hover:text-[#5C1F33] hover:bg-[#5C1F33]/5 transition-all duration-300"
                  >
                    Close
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* View All Services Button */}
        <div className="text-center">
          <Link to="/services">
            <motion.button
              whileHover={{ scale: 1.05, boxShadow: "0 10px 30px rgba(92, 31, 51, 0.25)" }}
              whileTap={{ scale: 0.98 }}
              className="px-8 py-4 bg-gradient-to-r from-[#5C1F33] to-[#800020] text-white rounded-xl text-sm font-semibold hover:from-[#6a2339] hover:to-[#900025] transition-all inline-flex items-center gap-2 shadow-lg"
            >
              View All Services
              <ArrowRight className="w-4 h-4" />
            </motion.button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default OurServices;
